import { CompleteMultipartUploadCommandOutput, S3Client } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import { fromCognitoIdentity } from "@aws-sdk/credential-provider-cognito-identity";
import { XhrHttpHandler } from "@aws-sdk/xhr-http-handler";
import { v4 as uuidv4 } from 'uuid';

export interface S3UploaderProcessData {
    promise: Promise<CompleteMultipartUploadCommandOutput>;
    abort: () => void;
    file: File;
}

export interface TempFileStoreAccessToken {
    IdentityPoolId: string;
    IdentityId: string;
    Token: string;
    BucketName: string;
    /** AWS region of the bucket, when the backend sends it. Otherwise the Cognito pool's region. */
    Region?: string;
}

/** Region prefix of a Cognito id, which AWS always formats as `<region>:<guid>`. */
function regionPrefix(id: string | undefined): string | undefined {
    return /^([a-z]{2}(?:-[a-z]+)+-\d+):/.exec(id ?? "")?.[1];
}

/** The region of the Cognito identity pool, taken from `IdentityId` or `IdentityPoolId`. */
export function resolveCognitoRegion(token: TempFileStoreAccessToken): string {
    const region = regionPrefix(token.IdentityId) ?? regionPrefix(token.IdentityPoolId);
    if (!region) {
        throw new Error(`Cannot determine Cognito region from upload token (IdentityPoolId: "${token.IdentityPoolId}").`);
    }
    return region;
}

/**
 * The region of the upload bucket. Prefers the token's `Region`; without it the bucket is assumed
 * to live in the Cognito pool's region.
 */
export function resolveS3Region(token: TempFileStoreAccessToken): string {
    return token.Region?.trim() || resolveCognitoRegion(token);
}

export interface TokenAcessor {
    (): Promise<TempFileStoreAccessToken>;
}

export type S3UploadEvent = {
    type: string,
    loaded: number,
    total: number,
};

export class S3Uploader {

    constructor() {
        this.abort = this.abort.bind(this);
    }

    public static startUpload(
        tokenAcessor: TokenAcessor,
        userId: string,
        fi: File,
        progressHandler: (d: S3UploadEvent) => void): S3UploaderProcessData {


        const s3Uploader = new S3Uploader();
        const prom = new Promise<CompleteMultipartUploadCommandOutput>((resolve, reject) => {

            s3Uploader.uploadToS3(
                tokenAcessor,
                fi,
                userId,
                progressHandler,
                (f) => resolve(f),
                (err: any) => {
                    reject(err);
                }
            );
        });
        return { promise: prom, abort: s3Uploader.abort, file: fi }

    };

    private uploadParallel: Upload | undefined = undefined;

    public abort() {
        if (this.uploadParallel) {
            this.uploadParallel.abort();
        }
    }

    async uploadToS3(tokenAcessor: TokenAcessor, file: File, userId: string, progress: (s: S3UploadEvent) => void,
        success: (s: CompleteMultipartUploadCommandOutput) => void, handleError: (s: any) => void) {

        let tokendata: TempFileStoreAccessToken;
        let cognitoRegion: string;
        let bucketRegion: string;
        try {
            tokendata = await tokenAcessor();
            cognitoRegion = resolveCognitoRegion(tokendata);
            bucketRegion = resolveS3Region(tokendata);
        } catch (error) {
            handleError(error as Error);
            return;
        }

        const creds = fromCognitoIdentity({
            identityId: tokendata.IdentityId,
            clientConfig: { region: cognitoRegion },
            logins: {
                "cognito-identity.amazonaws.com": tokendata.Token
            }
        });

        const client = new S3Client({

            region: bucketRegion,
            requestHandler: new XhrHttpHandler({}) as any,
            credentials: creds,

        })



        const ext = /^.+\.([^.]+)$/.exec(file.name);
        const extension = ext == null ? "" : "." + ext[1];

        const key = userId + "_" + uuidv4() + extension;

        const params = {
            Bucket: tokendata.BucketName,
            Key: key,
            Body: file

        };

        this.uploadParallel = new Upload({
            client: client,
            queueSize: 4,
            partSize: 5 * 1024 * 1024, // optional size of each part§
            leavePartsOnError: false,
            params: params
        });

        this.uploadParallel.on("httpUploadProgress", pr => {
            progress({
                type: "uploadprogress",
                loaded: pr.loaded!,
                total: pr.total!,
            });
        });

        try {
            const data = await this.uploadParallel.done();
            success(data);
        } catch (error: any) {
            handleError(error);
        }

    };

}
